import {
	GetBranchPaginated,
	BranchSearchCriteria,
} from '@matech/thebigpos-sdk'
import {
	InfiniteData,
	useInfiniteQuery,
	UseInfiniteQueryResult,
} from '@tanstack/react-query'

import { PaginationParams } from '../types'
import queryKeys from '../services/queryKeys'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'

type UsePaginatedBranchesBaseProps = {
	params: BranchSearchCriteria
	paginationParams: Omit<PaginationParams, 'pageNumber'>
	refetchOnMount?: boolean
	initialPageParam?: number
	staleTime?: number
}

type UsePaginatedBranchesProps<TSelected> =
	UsePaginatedBranchesBaseProps & {
		select?: (
			data: InfiniteData<GetBranchPaginated, number>
		) => TSelected
	}

const usePaginatedBranches = <
	TSelected = InfiniteData<GetBranchPaginated, number>,
>({
	params,
	paginationParams,
	refetchOnMount,
	initialPageParam = 1,
	staleTime = 0,
	select,
}: UsePaginatedBranchesProps<TSelected>): UseInfiniteQueryResult<
	TSelected,
	unknown
> => {
	return useInfiniteQuery({
		queryKey: [queryKeys.branches, params.searchText ?? ''],
		queryFn: async ({ pageParam: pageNumber }) => {
			const response = await TheBigPOSApi.searchBranches(params, {
				pageNumber,
				pageSize: paginationParams.pageSize,
				sortBy: paginationParams.sortBy,
				sortDirection: paginationParams.sortDirection,
			})

			return response.data
		},
		refetchOnMount,
		initialPageParam,
		getNextPageParam: (lastPage, allPages) => {
			if (!paginationParams.pageSize) return undefined

			if (lastPage.rows.length < paginationParams?.pageSize) {
				return undefined
			}

			return allPages.length + 1
		},
		staleTime,
		select,
	})
}

export default usePaginatedBranches
